import { forwardRef, type PropsWithChildren } from 'react';

import { Annotations } from './slots/annotations/Annotations.js';
import { Marker } from './slots/annotations/Marker.js';
import { AnnotationsTooltip } from './slots/annotations/Tooltip.js';
import { Track } from './slots/annotations/Track.js';
import { TimeseriesChartDownloadCSVSlot } from './slots/DownloadCSV.js';
import { Legend } from './slots/Legend.js';
import { Pan } from './slots/Pan.js';
import { Select } from './slots/Select.js';
import { Area } from './slots/shapes/Area.js';
import { Band } from './slots/shapes/Band.js';
import { Bar } from './slots/shapes/Bar.js';
import { Line } from './slots/shapes/Line.js';
import { ThresholdIndicator } from './slots/ThresholdIndicator.js';
import { Toolbar } from './slots/Toolbar.js';
import { Tooltip } from './slots/Tooltip.js';
import { XAxis } from './slots/XAxis.js';
import { YAxis } from './slots/YAxis.js';
import { Zoom } from './slots/Zoom.js';
import { TimeseriesChartRoot } from './TimeseriesChartRoot.js';
import type { TimeseriesChartProps } from './types/timeseries-props.js';
import type { TimeseriesChartRef } from './types/timeseries-ref.js';
import { Intent } from '../../core/slots/Intent/Intent.js';
import { EmptyStateSlot } from '../core/slots/chart-states/EmptyStateSlot.js';
import { ErrorStateSlot } from '../core/slots/chart-states/ErrorStateSlot.js';
import { ColorRule } from '../core/slots/ColorRule.js';
import type { _XYChartLegacyProps } from '../xy-chart-adoption/shared/types/legacy-props.js';
// eslint-disable-next-line no-restricted-imports
import { TimeseriesToXY } from '../xy-chart-adoption/timeseries/TimeseriesToXY.js';

export const TimeseriesChartSwitcher = forwardRef<
  TimeseriesChartRef,
  PropsWithChildren<TimeseriesChartProps & _XYChartLegacyProps>
>((props, forwardedRef) => {
  const { __useLegacy, ...rest } = props;
  if (__useLegacy) {
    return <TimeseriesChartRoot {...rest} ref={forwardedRef} />;
  }
  return <TimeseriesToXY {...rest} ref={forwardedRef} />;
});

(
  TimeseriesChartSwitcher as typeof TimeseriesChartSwitcher & {
    displayName: string;
  }
).displayName = 'TimeseriesChart';

/**
 * The `TimeseriesChart` visually represents data in sequential order of time.
 * It's used to observe trends, patterns, and fluctuations in the data over a
 * specific time period.
 * @public
 */
export const TimeseriesChart = Object.assign(TimeseriesChartSwitcher, {
  Band,
  Legend,
  YAxis,
  XAxis,
  Tooltip,
  Annotations,
  Line,
  Area,
  Bar,
  ThresholdIndicator,
  Select,
  Intent,
  EmptyState: EmptyStateSlot,
  ErrorState: ErrorStateSlot,
  ColorRule,
  Toolbar,
  DownloadCSV: TimeseriesChartDownloadCSVSlot,
  Pan,
  Zoom,
});

/**
 * Annotations Slots
 * @public
 */
export const TimeseriesAnnotations = Object.assign(
  {},
  {
    Marker,
    Track,
    Tooltip: AnnotationsTooltip,
  },
);
